import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Mail, Building2, MapPin, Briefcase, Scale, FileText, Sparkles, Check, EyeOff, Trash2, AlertCircle, Calendar as CalendarIcon, Home, Loader2 } from 'lucide-react';
import { formatDate } from '../utils/dateHelpers';
import {
  QuickCreateEmpresa,
  QuickCreateProprietaria,
  QuickCreateArea,
  QuickCreateOferta,
  SelectComCriar,
} from './QuickCreateModals';
import AIContextChat from './ai/AIContextChat';
import EmailRelationshipCard from './relationships/EmailRelationshipCard';
import EventCreateModal from './calendar/EventCreateModal';
import { buildEventFromEmail } from '../utils/buildEventFromEmail';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabaseClient';

const TIPOS_CONTEUDO = [
  { value: 'negociacao', label: 'Negociação', icon: '🤝' },
  { value: 'juridico', label: 'Jurídico', icon: '⚖️' },
  { value: 'operacional', label: 'Operacional', icon: '📋' },
  { value: 'estrategico', label: 'Estratégico', icon: '🎯' },
  { value: 'relacionamento', label: 'Relacionamento', icon: '👋' },
  { value: 'informativo', label: 'Informativo', icon: 'ℹ️' },
  { value: 'outro', label: 'Outro', icon: '❔' },
];

/**
 * EmailDetailDrawer v2 (Sessão 4.7).
 * 
 * Novidade: dropdowns incluem opção "+ Criar nova..." que abre modal quick-create
 * sem sair do contexto. Ao criar, entidade vira automaticamente o valor do dropdown.
 * 
 * Rules of Hooks (CLAUDE.md Regra 6): todos os useState/useEffect/useMemo antes
 * de qualquer early return.
 */
const EmailDetailDrawer = ({ 
  email, 
  empresas: empresasProp, 
  areas: areasProp, 
  ofertas: ofertasProp, 
  onAplicarVinculos, 
  onIgnorar, 
  onLixo, 
  onClose,
  onRefreshEntidades, // callback para recarregar listas após criação inline
  onVerTimelineOferta, // opcional: abre EmailsHubModal modo=oferta
}) => {
  // === TODOS os hooks ANTES de qualquer early return ===
  const [empresaId, setEmpresaId] = useState('');
  const [empresaProprietariaId, setEmpresaProprietariaId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [ofertaId, setOfertaId] = useState('');
  // 06/mai/26: vínculo opcional com imóvel específico (emails_gmail.imovel_id)
  const [imovelId, setImovelId] = useState('');
  const [tipoConteudo, setTipoConteudo] = useState('');
  const [contatoRepresenta, setContatoRepresenta] = useState('');
  const [processando, setProcessando] = useState(false);
  const [erroLocal, setErroLocal] = useState(null);

  // Estado local das listas — começa com props, atualiza ao criar inline
  const [empresasLocal, setEmpresasLocal] = useState(empresasProp || []);
  const [areasLocal, setAreasLocal] = useState(areasProp || []);
  const [ofertasLocal, setOfertasLocal] = useState(ofertasProp || []);

  // Qual modal quick-create está aberto (null = nenhum)
  const [quickCreate, setQuickCreate] = useState(null);
  // Valores: null | 'empresa' | 'proprietaria' | 'area' | 'oferta'

  // Onda 4 — agendar reunião a partir deste email
  const { user } = useAuth();
  const [eventPrefill, setEventPrefill] = useState(null);

  // Sincroniza props → local quando mudam (ex: refresh do pai)
  useEffect(() => { setEmpresasLocal(empresasProp || []); }, [empresasProp]);
  useEffect(() => { setAreasLocal(areasProp || []); }, [areasProp]);
  useEffect(() => { setOfertasLocal(ofertasProp || []); }, [ofertasProp]);

  // Resetar seleção quando email muda
  useEffect(() => {
    if (email) {
      setEmpresaId(email.empresa_id || '');
      setEmpresaProprietariaId(email.empresa_proprietaria_id || '');
      setAreaId(email.area_mencionada_id || '');
      setOfertaId(email.oferta_id || '');
      setImovelId(email.imovel_id || '');
      setTipoConteudo(email.tipo_conteudo || '');
      setContatoRepresenta('');
      setErroLocal(null);
    }
  }, [email?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ofertas filtradas por empresa + área
  const ofertasFiltradas = useMemo(() => {
    return (ofertasLocal || []).filter(o => {
      if (empresaId && o.empresa_id !== empresaId) return false;
      if (areaId && o.area_id !== areaId) return false;
      return true;
    });
  }, [ofertasLocal, empresaId, areaId]);

  // Empresa e área resolvidas para QuickCreateOferta
  const empresaResolvida = useMemo(
    () => empresasLocal.find(e => e.id === empresaId) || null,
    [empresasLocal, empresaId]
  );
  const areaResolvida = useMemo(
    () => areasLocal.find(a => a.id === areaId) || null,
    [areasLocal, areaId]
  );

  // Advogado? 
  const isAdvogado = email?.remetente_tipo === 'advogado';

  // === DEPOIS dos hooks: early return ===
  if (!email) return null;

  const tipoRemetenteLabel = {
    humano_interno: '👥 Equipe Aston',
    humano_externo: '🏢 Externo',
    advogado: '⚖️ Advogado',
    ata_reuniao: '📝 Ata de reunião',
    ferramenta_automatica: '🤖 Automação',
  }[email.remetente_tipo] || '❔ Desconhecido';

  // === Handlers de criação inline ===

  const handleEmpresaCriada = (empresaNova) => {
    setEmpresasLocal(prev => [...prev, empresaNova].sort((a, b) => 
      (a.empresa || '').localeCompare(b.empresa || '')
    ));
    setEmpresaId(empresaNova.id);
    setQuickCreate(null);
    if (onRefreshEntidades) onRefreshEntidades();
  };

  const handleProprietariaCriada = (empresaNova) => {
    setEmpresasLocal(prev => [...prev, empresaNova].sort((a, b) => 
      (a.empresa || '').localeCompare(b.empresa || '')
    ));
    setEmpresaProprietariaId(empresaNova.id);
    setQuickCreate(null);
    if (onRefreshEntidades) onRefreshEntidades();
  };

  const handleAreaCriada = (areaNova) => {
    setAreasLocal(prev => [...prev, areaNova].sort((a, b) => 
      (a.nome || '').localeCompare(b.nome || '')
    ));
    setAreaId(areaNova.id);
    setQuickCreate(null);
    if (onRefreshEntidades) onRefreshEntidades();
  };

  const handleOfertaCriada = (ofertaNova) => {
    setOfertasLocal(prev => [...prev, ofertaNova]);
    setOfertaId(ofertaNova.id);
    setQuickCreate(null);
    if (onRefreshEntidades) onRefreshEntidades();
  };

  // === Handlers de ações principais ===

  const handleAplicar = async () => {
    setProcessando(true);
    setErroLocal(null);
    try {
      const vinculos = {
        empresa_id: empresaId || null,
        empresa_proprietaria_id: empresaProprietariaId || null,
        area_mencionada_id: areaId || null,
        oferta_id: ofertaId || null,
        imovel_id: imovelId || null,
        tipo_conteudo: tipoConteudo || null,
        resolver: true,
      };
      if (isAdvogado && contatoRepresenta) {
        vinculos.contato_representa_empresa_id = contatoRepresenta;
      }
      await onAplicarVinculos(email.id, vinculos);
    } catch (err) {
      setErroLocal(err.message);
    } finally {
      setProcessando(false);
    }
  };

  const handleIgnorar = async () => {
    if (!window.confirm('Marcar como não relevante? Email some da Inbox (permanece no histórico).')) return;
    setProcessando(true);
    try { await onIgnorar(email.id); }
    catch (err) { setErroLocal(err.message); }
    finally { setProcessando(false); }
  };

  const handleLixo = async () => {
    if (!window.confirm('Descartar permanentemente como lixo?')) return;
    setProcessando(true);
    try { await onLixo(email.id); }
    catch (err) { setErroLocal(err.message); }
    finally { setProcessando(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-[10002] pointer-events-none">
        <div 
          className="absolute inset-0 bg-black/30 pointer-events-auto transition-opacity"
          onClick={onClose}
        />

        <div className="absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col pointer-events-auto animate-slide-in-right">
          
          {/* Header */}
          <div className="flex-shrink-0 p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 flex-grow min-w-0">
                <div className="bg-amber-100 p-1.5 rounded-lg flex-shrink-0">
                  <Mail className="text-amber-600" size={18} />
                </div>
                <div className="min-w-0 flex-grow">
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">Detalhe do email</div>
                  <div className="text-sm font-bold text-slate-800 break-words">
                    {email.subject || '(sem assunto)'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setEventPrefill(buildEventFromEmail(email))}
                  className="text-[10px] px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200 flex items-center gap-1"
                  title="Criar evento na agenda a partir deste email"
                >
                  <CalendarIcon size={11} /> Agendar Reunião
                </button>
                {email.oferta_id && onVerTimelineOferta && (
                  <button
                    onClick={() => onVerTimelineOferta(email.oferta_id, email.ofertas?.nome)}
                    className="text-[10px] px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 flex items-center gap-1"
                    title="Ver timeline completa desta oferta"
                  >
                    📋 Timeline
                  </button>
                )}
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Corpo */}
          <div className="flex-grow overflow-y-auto">
            
            {/* Cabeçalho do email */}
            <div className="p-4 border-b border-slate-100 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-700 rounded">
                  {email.direcao === 'entrada' ? '📥 Recebido' : '📤 Enviado'}
                </span>
                <span className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                  {tipoRemetenteLabel}
                </span>
                <span className="text-[10px] text-slate-400 ml-auto">
                  {formatDate(email.data_envio)}
                </span>
              </div>

              <div className="text-xs space-y-1 mt-2">
                <div className="flex gap-2">
                  <span className="text-slate-500 font-medium min-w-[40px]">De:</span>
                  <span className="text-slate-800 break-all">{email.from_email || '—'}</span>
                </div>
                {email.to_emails && email.to_emails.length > 0 && (
                  <div className="flex gap-2">
                    <span className="text-slate-500 font-medium min-w-[40px]">Para:</span>
                    <span className="text-slate-800 break-all">{email.to_emails.join(', ')}</span>
                  </div>
                )}
                {email.cc_emails && email.cc_emails.length > 0 && (
                  <div className="flex gap-2">
                    <span className="text-slate-500 font-medium min-w-[40px]">CC:</span>
                    <span className="text-slate-800 break-all">{email.cc_emails.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* G5E — Card de explicabilidade do relacionamento do remetente */}
            <EmailRelationshipCard
              fromEmail={email.from_email}
              toEmails={email.to_emails}
              ccEmails={email.cc_emails}
            />

            {/* Conteúdo */}
            <div className="p-4 border-b border-slate-100">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-2 flex items-center gap-1">
                <FileText size={11} /> Conteúdo
              </div>
              <div className="text-xs text-slate-800 whitespace-pre-wrap bg-slate-50 p-3 rounded border border-slate-200 max-h-[300px] overflow-y-auto">
                {email.body_text || '(sem conteúdo)'}
              </div>
            </div>

            {/* Sugestões IA */}
            {email.areas?.nome && (
              <div className="p-4 border-b border-slate-100 bg-indigo-50/50">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700 mb-2 flex items-center gap-1">
                  <Sparkles size={11} /> Detectado automaticamente
                </div>
                <div className="text-xs text-indigo-900">
                  ✨ Este email menciona a área: <strong>{email.areas.nome}</strong>
                  {email.empresas?.empresa && <><br />🏢 Empresa do remetente: <strong>{email.empresas.empresa}</strong></>}
                  {email.empresa_proprietaria?.empresa && <><br />🏠 Proprietária: <strong>{email.empresa_proprietaria.empresa}</strong></>}
                </div>
              </div>
            )}

            {/* Formulário de vinculação com opções de criar inline */}
            <div className="p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-700 flex items-center gap-1">
                🔗 Vínculos do email
              </div>

              {/*
                06/mai/26 — Ordem dos campos:
                  Empresa → Oferta → Área → Imóvel → Empresa proprietária → Tipo de conteúdo.
                Imóvel usa SelectImovel (busca por nome / matrícula / endereço / inscrição municipal).
              */}

              {/* Empresa */}
              <SelectComCriar
                label="Empresa"
                icon={Building2}
                value={empresaId}
                onChange={setEmpresaId}
                onCreateNew={() => setQuickCreate('empresa')}
                options={empresasLocal}
                valueKey="id"
                labelKey="empresa"
                placeholder="— Escolher empresa —"
                helperText="Geralmente a empresa compradora/cliente do negócio"
                createLabel="+ Criar nova empresa..."
              />

              {/* Oferta (filtrada por empresa + área) */}
              <SelectComCriar
                label="Oferta"
                icon={Briefcase}
                value={ofertaId}
                onChange={setOfertaId}
                onCreateNew={() => {
                  if (!empresaId || !areaId) {
                    setErroLocal('Preencha Empresa e Área antes de criar oferta (sistema gera nome automático EMPRESA: ÁREA).');
                    return;
                  }
                  setQuickCreate('oferta');
                }}
                options={ofertasFiltradas}
                valueKey="id"
                labelKey="nome"
                placeholder="— Escolher oferta —"
                helperText={
                  empresaId && areaId
                    ? `${ofertasFiltradas.length} ofertas da empresa+área selecionadas. Pode criar nova se não existir.`
                    : 'Preencha empresa e/ou área para filtrar (e habilitar criação).'
                }
                createLabel="+ Criar nova oferta (EMPRESA: ÁREA)..."
              />

              {/* Área */}
              <SelectComCriar
                label="Área mencionada"
                icon={MapPin}
                value={areaId}
                onChange={setAreaId}
                onCreateNew={() => setQuickCreate('area')}
                options={areasLocal}
                valueKey="id"
                labelKey="nome"
                placeholder="— Escolher área —"
                createLabel="+ Criar nova área..."
              />

              {/* Imóvel (06/mai/26) */}
              <SelectImovel value={imovelId} onChange={setImovelId} />

              {/* Empresa proprietária */}
              <SelectComCriar
                label="Empresa proprietária (vendedor)"
                icon={Building2}
                value={empresaProprietariaId}
                onChange={setEmpresaProprietariaId}
                onCreateNew={() => setQuickCreate('proprietaria')}
                options={empresasLocal}
                valueKey="id"
                labelKey="empresa"
                placeholder="— (opcional) —"
                helperText="Quando email envolve o dono do terreno (ex: FUNSAI é proprietária de Gama Lobo)"
                createLabel="+ Criar novo proprietário..."
              />

              {/* Tipo de conteúdo */}
              <div>
                <label className="text-[11px] font-semibold text-slate-700 flex items-center gap-1 mb-1">
                  <FileText size={11} /> Tipo de conteúdo
                </label>
                <div className="flex flex-wrap gap-1">
                  {TIPOS_CONTEUDO.map(t => (
                    <button
                      key={t.value}
                      onClick={() => setTipoConteudo(tipoConteudo === t.value ? '' : t.value)}
                      className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                        tipoConteudo === t.value 
                          ? 'bg-indigo-600 text-white border-indigo-700' 
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Advogado representa empresa */}
              {isAdvogado && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded space-y-2">
                  <div className="text-[11px] font-semibold text-purple-900 flex items-center gap-1">
                    <Scale size={11} /> Este advogado representa qual empresa?
                  </div>
                  <select
                    value={contatoRepresenta}
                    onChange={(e) => setContatoRepresenta(e.target.value)}
                    className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded bg-white"
                  >
                    <option value="">— Não atualizar agora —</option>
                    {empresasLocal.map(e => (
                      <option key={e.id} value={e.id}>{e.empresa}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-purple-700">
                    Preencher só uma vez; o sistema aprende para emails futuros desse advogado.
                  </div>
                </div>
              )}

              {erroLocal && (
                <div className="p-2 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
                  <AlertCircle size={12} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-red-800">{erroLocal}</span>
                </div>
              )}
            </div>

            {/* Context Loop AI-native Fase 1 (2026-04-26): chat inline para ensinar a IA sobre este email */}
            <div className="p-4 pt-0">
              <AIContextChat
                scope={{
                  screen: 'email_detail',
                  email_ids: [email.id],
                  empresa_id: email.empresa_id || null,
                  oferta_id: email.oferta_id || null,
                  area_id: email.area_mencionada_id || null,
                }}
                onActionsApplied={() => { if (onRefreshEntidades) onRefreshEntidades(); }}
                defaultCollapsed={true}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
            <button
              onClick={handleAplicar}
              disabled={processando}
              className="flex-grow px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-1"
            >
              <Check size={14} /> {processando ? 'Aplicando...' : 'Aplicar vínculos'}
            </button>
            <button
              onClick={handleIgnorar}
              disabled={processando}
              className="px-3 py-2 text-slate-600 hover:bg-slate-200 rounded text-xs flex items-center gap-1"
              title="Email não é relevante"
            >
              <EyeOff size={12} /> Ignorar
            </button>
            <button
              onClick={handleLixo}
              disabled={processando}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded text-xs flex items-center gap-1"
              title="Descartar permanentemente"
            >
              <Trash2 size={12} /> Lixo
            </button>
          </div>
        </div>
      </div>

      {/* Modais quick-create sobrepostos (z-10003) */}
      {quickCreate === 'empresa' && (
        <QuickCreateEmpresa
          onClose={() => setQuickCreate(null)}
          onCreated={handleEmpresaCriada}
        />
      )}
      {quickCreate === 'proprietaria' && (
        <QuickCreateProprietaria
          onClose={() => setQuickCreate(null)}
          onCreated={handleProprietariaCriada}
        />
      )}
      {quickCreate === 'area' && (
        <QuickCreateArea
          onClose={() => setQuickCreate(null)}
          onCreated={handleAreaCriada}
        />
      )}
      {quickCreate === 'oferta' && (
        <QuickCreateOferta
          onClose={() => setQuickCreate(null)}
          onCreated={handleOfertaCriada}
          empresa={empresaResolvida}
          area={areaResolvida}
        />
      )}

      {/* Onda 4 — modal de criação de evento alimentado pelo email atual */}
      <EventCreateModal
        open={!!eventPrefill}
        onClose={() => setEventPrefill(null)}
        onCreated={() => setEventPrefill(null)}
        prefill={eventPrefill}
        currentUserEmail={user?.email}
      />
    </>
  );
};

/**
 * SelectImovel — dropdown searchable de imóveis (06/mai/26).
 *
 * Pesquisa por nome, matrícula, endereço ou inscrição municipal (debounce 300ms).
 * Carrega o label do imóvel já selecionado se `value` veio preenchido.
 * Display: `{nome} — Mat. {matricula}` + linha menor "Área: {area_nome}" quando o
 * imóvel pertence a um lote vinculado a uma área.
 */
const SelectImovel = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const wrapperRef = useRef(null);

  // Carrega o label do imóvel atualmente selecionado.
  useEffect(() => {
    if (!value) { setSelectedLabel(null); return; }
    let cancel = false;
    setLoadingSelected(true);
    (async () => {
      const { data } = await supabase
        .from('imoveis')
        .select('id, nome, matricula, endereco, numero, lote:lotes(area:areas(nome))')
        .eq('id', value)
        .maybeSingle();
      if (cancel) return;
      if (data) {
        setSelectedLabel({
          id: data.id,
          nome: data.nome,
          matricula: data.matricula,
          endereco: [data.endereco, data.numero].filter(Boolean).join(' '),
          area_nome: data.lote?.area?.nome || null,
        });
      } else {
        setSelectedLabel(null);
      }
      setLoadingSelected(false);
    })();
    return () => { cancel = true; };
  }, [value]);

  // Busca debounce.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      const like = `%${q}%`;
      const { data } = await supabase
        .from('imoveis')
        .select('id, nome, matricula, endereco, numero, bairro, area_terreno_m2, inscricao_municipal, lote:lotes(area:areas(id, nome))')
        .or(`nome.ilike.${like},matricula.ilike.${like},endereco.ilike.${like},inscricao_municipal.ilike.${like}`)
        .order('nome')
        .limit(20);
      if (!cancelled) {
        setResults(data || []);
        setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, open]);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleSelect = (imovel) => {
    onChange(imovel.id);
    setSelectedLabel({
      id: imovel.id,
      nome: imovel.nome,
      matricula: imovel.matricula,
      endereco: [imovel.endereco, imovel.numero].filter(Boolean).join(' '),
      area_nome: imovel.lote?.area?.nome || null,
    });
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSelectedLabel(null);
    setQuery('');
  };

  return (
    <div ref={wrapperRef}>
      <label className="text-[11px] font-semibold text-slate-700 flex items-center gap-1 mb-1">
        <Home size={11} /> Imóvel
      </label>
      <div className="relative">
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full text-left text-xs px-2 py-1.5 border border-slate-300 rounded bg-white hover:border-slate-400 flex items-center gap-1"
          >
            {loadingSelected ? (
              <span className="text-slate-400 flex items-center gap-1">
                <Loader2 size={10} className="animate-spin" /> Carregando...
              </span>
            ) : selectedLabel ? (
              <span className="flex-grow truncate">
                <span className="text-slate-800">{selectedLabel.nome || '—'}</span>
                {selectedLabel.matricula && (
                  <span className="text-slate-500"> — Mat. {selectedLabel.matricula}</span>
                )}
              </span>
            ) : (
              <span className="text-slate-400 flex-grow">— (opcional) buscar imóvel —</span>
            )}
            {value && !loadingSelected && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e); }}
                className="text-slate-400 hover:text-red-600 px-1 cursor-pointer"
                title="Remover vínculo"
              >
                <X size={11} />
              </span>
            )}
          </button>
        )}

        {open && (
          <div className="border border-indigo-300 rounded bg-white shadow-sm">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome, matrícula, endereço ou inscrição..."
              className="w-full text-xs px-2 py-1.5 border-b border-slate-200 outline-none focus:border-indigo-400"
              autoFocus
            />
            <div className="max-h-56 overflow-y-auto">
              {searching && (
                <div className="text-[11px] text-slate-500 flex items-center gap-1 px-2 py-2">
                  <Loader2 size={10} className="animate-spin" /> Buscando...
                </div>
              )}
              {!searching && query.trim().length < 2 && (
                <div className="text-[11px] text-slate-400 italic px-2 py-2">
                  Digite no mínimo 2 caracteres.
                </div>
              )}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <div className="text-[11px] text-slate-400 italic px-2 py-2">Nenhum imóvel encontrado.</div>
              )}
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => handleSelect(r)}
                  className="w-full text-left text-xs px-2 py-1.5 hover:bg-indigo-50 border-b border-slate-100 last:border-b-0"
                >
                  <div className="text-slate-800 truncate">
                    <Home size={10} className="inline mr-1 text-slate-400" />
                    <span className="font-medium">{r.nome || '—'}</span>
                    {r.matricula && <span className="text-slate-500"> — Mat. {r.matricula}</span>}
                  </div>
                  {(r.lote?.area?.nome || r.endereco) && (
                    <div className="text-[10px] text-slate-400 truncate ml-4">
                      {r.lote?.area?.nome && <>Área: {r.lote.area.nome}</>}
                      {r.lote?.area?.nome && r.endereco && ' · '}
                      {r.endereco && <>{[r.endereco, r.numero].filter(Boolean).join(' ')}</>}
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="flex justify-end px-2 py-1 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setOpen(false); setQuery(''); }}
                className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-0.5"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="text-[10px] text-slate-500 mt-0.5">
        Imóvel específico mencionado no email (ex: matrícula, endereço)
      </div>
    </div>
  );
};

export default EmailDetailDrawer;
